import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FadeIn } from '../components/UI';

// =============================================
// SMTP BACKEND URL AYARI
// server/index.js'i başlattığınızda bu URL çalışır.
// Production ortamında kendi domain adresinizle değiştirin.
// =============================================
const API_URL = process.env.REACT_APP_API_URL || '/';

// =============================================
// CLOUDFLARE TURNSTILE (bot koruması)
// Site key public'tir, build sırasında bundle'a gömülür — sorun değil.
// Asıl doğrulama sunucuda secret key ile yapılır (server/index.js).
// =============================================
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const contactItems = [
  {
    icon: (
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    ),
    title: 'Telefon',
    value: '+90 (533) 207 19 00',
    href: 'tel:+905332071900',
  },
  {
    icon: (
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    title: 'E-posta',
    value: 'hello@cevfin.com',
    href: 'mailto:hello@cevfin.com',
  },
  {
    icon: (
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
    title: 'Merkez Ofis',
    value: 'CEV Finansal Teknolojiler A.Ş.\nÇifte Havuzlar Mah. Eski Londra Asfaltı Cad.\nKuluçka Mrk. A1 Blok No: 151/1c\nİç Kapı No: B35 Esenler-İstanbul',
    href: null,
  },
];

export default function IletisimPage() {
  const location = useLocation();
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    website: '', // honeypot — gerçek kullanıcı görmez, bot doldurur
  });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [token, setToken] = useState('');

  const widgetHostRef = useRef(null);
  const widgetIdRef = useRef(null);
  const openedAtRef = useRef(Date.now());

  // Yetenekler sayfasından gelen mesajı form'a yaz
  useEffect(() => {
    if (location.state?.message) {
      setForm(prev => ({ ...prev, message: location.state.message }));
    }
  }, [location.state]);

  // Turnstile widget'ını yükle ve render et.
  // Script'i explicit modda yüklüyoruz: implicit render (class="cf-turnstile")
  // React'in mount sırasıyla yarışıyor ve widget bazen hiç çizilmiyor.
  useEffect(() => {
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled) return;
      if (!window.turnstile || !widgetHostRef.current) return;
      if (widgetIdRef.current !== null) return; // zaten render edildi

      widgetIdRef.current = window.turnstile.render(widgetHostRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        language: 'tr',
        callback: (nextToken) => setToken(nextToken),
        'expired-callback': () => setToken(''),
        'error-callback': () => setToken(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
      return undefined;
    }

    let script = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', renderWidget);

    return () => {
      cancelled = true;
      script.removeEventListener('load', renderWidget);
    };
  }, []);

  // Turnstile token'ı tek kullanımlıktır; her gönderimden sonra sıfırlanmalı,
  // yoksa ikinci deneme her zaman 403 alır.
  const resetTurnstile = () => {
    setToken('');
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setStatus('error');
      setErrorMsg('Lütfen güvenlik doğrulamasını tamamlayın.');
      setTimeout(() => setStatus(null), 5000);
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          turnstileToken: token,
          elapsed: Date.now() - openedAtRef.current,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus('success');
        setForm({ name: '', company: '', email: '', phone: '', message: '', website: '' });
        openedAtRef.current = Date.now();
        setTimeout(() => setStatus(null), 7000);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Bir hata oluştu. Lütfen tekrar deneyin.');
        setTimeout(() => setStatus(null), 5000);
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Sunucuya bağlanılamadı. Lütfen daha sonra tekrar deneyin.');
      setTimeout(() => setStatus(null), 5000);
    } finally {
      resetTurnstile();
    }
  };

  return (
    <div className="page-enter" style={{ paddingTop: 72 }}>
      <section className="section">
        <div className="container">

          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <FadeIn>
              <div className="section-label">İletişim</div>
              <h2 className="section-title">Ekosistemimiz İçin <span>Demo Talep Edin</span></h2>
              <p className="section-desc" style={{ margin: '0 auto' }}>
                Uzmanlarımız işletmenize uygun modüller için iletişime geçecek.
              </p>
            </FadeIn>
          </div>

          <div className="contact-grid">
            {/* Left: Info */}
            <FadeIn>
              <div className="contact-info">
                <h2>Sizinle Konuşmak İsteriz</h2>
                <p>
                  CevFİN'in sunduğu holistik çözümler hakkında detaylı bilgi almak veya ücretsiz demo
                  istemek için formu doldurun. Ekibimiz en kısa sürede geri dönüş yapacaktır.
                </p>

                {contactItems.map((item, i) => (
                  <div key={i} className="contact-item">
                    <div className="icon-box">{item.icon}</div>
                    <div className="contact-item-text">
                      <h5>{item.title}</h5>
                      {item.href ? (
                        <p><a href={item.href} style={{ color: 'var(--blue-500)', textDecoration: 'none' }}>{item.value}</a></p>
                      ) : (
                        <p style={{ whiteSpace: 'pre-line' }}>{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Working hours */}
                <div style={{
                  background: 'var(--blue-50)',
                  border: '1px solid var(--blue-100)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 20,
                  marginTop: 24,
                }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--blue-950)', marginBottom: 12 }}>
                    🕐 Çalışma Saatleri
                  </div>
                  {[
                    { day: 'Pazartesi – Cuma', hours: '09:00 – 18:00' },
                    { day: 'Teknik Destek', hours: '7/24 Aktif' },
                  ].map((row, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.875rem', marginBottom: 6 }}>
                      <span style={{ color: 'var(--slate-600)' }}>{row.day}</span>
                      <span style={{ color: 'var(--blue-600)', fontWeight: 600 }}>{row.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

            {/* Right: Form */}
            <FadeIn delay={0.2}>
              <div className="card form-card">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--blue-950)', marginBottom: 24 }}>
                  Demo Talep Formu
                </h3>

                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Ad Soyad <span>*</span></label>
                      <input
                        type="text"
                        name="name"
                        className="form-input"
                        placeholder="İsminiz"
                        value={form.name}
                        onChange={handleChange}
                        maxLength={100}
                        required
                        onInvalid={(e) =>
                        e.target.setCustomValidity('Bu alan zorunludur')
                      } onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Firma <span>*</span></label>
                      <input
                        type="text"
                        name="company"
                        className="form-input"
                        placeholder="Şirket Adı"
                        value={form.company}
                        onChange={handleChange}
                        maxLength={150}
                        required
                        onInvalid={(e) =>
                          e.target.setCustomValidity('Bu alan zorunludur')
                        } onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">E-posta <span>*</span></label>
                      <input
                        type="email"
                        name="email"
                        className="form-input"
                        placeholder="email@sirket.com"
                        value={form.email}
                        onChange={handleChange}
                        maxLength={254}
                        required
                        onInvalid={(e) =>
                        e.target.setCustomValidity('Bu alan zorunludur')
                      } onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefon<span>*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-input"
                        placeholder="+90 5XX XXX XX XX"
                        value={form.phone}
                        onChange={handleChange}
                        maxLength={30}
                        required
                        onInvalid={(e) =>
                        e.target.setCustomValidity('Bu alan zorunludur')
                      } onInput={(e) => e.target.setCustomValidity('')}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Mesajınız<span>*</span></label>
                    <textarea
                      name="message"
                      className="form-input"
                      placeholder=""
                      value={form.message}
                      onChange={handleChange}
                      maxLength={2000}
                      required
                      onInvalid={(e) =>
                        e.target.setCustomValidity('Bu alan zorunludur')
                      } onInput={(e) => e.target.setCustomValidity('')}
                    />
                  </div>

                  {/* Honeypot: ekran dışında, sekmeyle ulaşılamaz, okuyuculardan gizli.
                      Gerçek kullanıcı asla dolduramaz; dolu gelirse sunucu isteği sessizce düşürür. */}
                  <div className="hp-field" aria-hidden="true">
                    <label htmlFor="website">Web sitesi (doldurmayınız)</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={form.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div ref={widgetHostRef} className="turnstile-wrap" />

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', padding: 14 }}
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? (
                      <>
                        <svg className="spin" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                        </svg>
                        Demo Talep Gönder
                      </>
                    )}
                  </button>

                  {status === 'success' && (
                    <div className="form-msg success" style={{ display: 'flex' }}>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18, flexShrink: 0 }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Talebiniz alındı! En kısa sürede sizinle iletişime geçeceğiz.
                    </div>
                  )}

                  {status === 'error' && (
                    <div className="form-msg error" style={{ display: 'flex' }}>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18, flexShrink: 0 }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      {errorMsg}
                    </div>
                  )}
                </form>
              </div>
            </FadeIn>
          </div>

        </div>
      </section>
    </div>
  );
}
